import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useActiveUserTenant } from 'octiv-context/ActiveUserTenant';
import { request } from 'octiv-utilities/Request';

export const useClassRecurringBookingsFind = (input, options) => {
  const { tenantId } = useActiveUserTenant();
  const { filter = {}, paging = {}, include = undefined } = input || {};

  return useQuery(
    ['classRecurringBookings', 'find', input],
    () =>
      request({
        method: 'get',
        url: `/api/class-recurring-bookings`,
        params: {
          filter: {
            tenantId: filter.tenantId || tenantId,
            locationId: filter.locationId,
            coachId: filter.trainerId,
            status: filter.status,
            isSession: filter.isSession,
            sort: filter.sort,
            order: filter.order,
          },
          include,
          ...paging,
        },
      }),
    options
  );
};

export const useClassRecurringBookingsCreate = (options) => {
  const queryClient = useQueryClient();
  const { tenantId } = useActiveUserTenant();

  return useMutation({
    ...options,
    mutationFn: (input) => {
      const data = input || {};

      return request({
        method: 'post',
        url: `/api/class-recurring-bookings`,
        data: {
          tenantId: data.tenantId || tenantId,
          classId: data.classId,
          userId: data.userId,
          daysOfTheWeek: data.daysOfTheWeek,
          endingOnDate: data.endingOnDate,
        },
      });
    },
    onSuccess: (...res) => {
      queryClient.invalidateQueries({ queryKey: ['classRecurringBookings'] });

      options?.onSuccess?.(...res);
    },
  });
};

export const useClassRecurringBookingsFindById = (input, options) => {
  const { id } = input || {};

  return useQuery(
    ['classRecurringBookings', 'findById', input],
    () =>
      request({
        method: 'get',
        url: `/api/class-recurring-bookings/${id}`,
      }),
    options
  );
};

export const useClassRecurringBookingsUpdate = (options) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (input) => {
      const { id, ...data } = input || {};

      return request({
        method: 'put',
        url: `/api/class-recurring-bookings/${id}`,
        data: {
          daysOfTheWeek: data.daysOfTheWeek,
          endingOnDate: data.endingOnDate,
        },
      });
    },
    onSuccess: (...res) => {
      queryClient.invalidateQueries({ queryKey: ['classRecurringBookings'] });

      options?.onSuccess?.(...res);
    },
  });
};

export const useClassRecurringBookingsDelete = (options) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (input) => {
      const { id } = input || {};

      return request({
        method: 'delete',
        url: `/api/class-recurring-bookings/${id}`,
      });
    },
    onSuccess: (...res) => {
      queryClient.invalidateQueries({ queryKey: ['classRecurringBookings'] });

      options?.onSuccess?.(...res);
    },
  });
};

export const useClassRecurringBookingsUpdateReActivate = (options) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (input) => {
      const { id } = input || {};

      return request({
        method: 'put',
        url: `/api/class-recurring-bookings/${id}/re-activate`,
      });
    },
    onSuccess: (...res) => {
      queryClient.invalidateQueries({ queryKey: ['classRecurringBookings'] });

      options?.onSuccess?.(...res);
    },
  });
};

export const useClassRecurringBookingsUpdateDeactivate = (options) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (input) => {
      const { id } = input || {};

      return request({
        method: 'put',
        url: `/api/class-recurring-bookings/${id}/deactivate`,
      });
    },
    onSuccess: (...res) => {
      queryClient.invalidateQueries({ queryKey: ['classRecurringBookings'] });

      options?.onSuccess?.(...res);
    },
  });
};
